import { useMutation, useQuery } from '@tanstack/react-query';
import { getData, postData } from '../utils/api';
import { ErrorObject } from '../@types/api';
import { enqueueSnackbar } from 'notistack';
import { useLogin } from './useLogin';
import useLocalization from '../hooks/useLocalization';
import { PaginatedData } from '../@types';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { useExtendedSnackbar } from '../hooks/useExtendedSnackbar';
import { useMemo, useState } from 'react';
import { FieldDefinition } from '../components/shared/DynamicFormFields';
import { getTimeZone } from '../utils/timeZones';
import { getEnabledFields } from '../utils';
import { SignUpFormField, useMarketplaceConfig } from '../queries/marketplace';
import { TFunction } from 'i18next';
import z from 'zod';
import { createRegisterFieldsSchemas, FieldSchemaKey } from '../schemas/registerFieldsSchemas';
import { DEFAULT_MY_ACCOUNT_FIELDS } from '../constants';
import { BOUserFormData } from '../hooks/useCreateBOUsersForm';
import { RegisterFormData } from '../hooks/registerForm.shared';
import { getRegistrationMetadata, RegistrationMetadata } from '../utils/registrationMetadata';
import { getTurnstileHeaders } from '../utils/turnstile';

type RegisterResponse = {
  success: boolean;
  message?: string;
};

type RegisterSubmitData = Omit<RegisterFormData, 'ageVerified'> & { turnstileToken?: string };
type RegisterPayload = Omit<RegisterSubmitData, 'turnstileToken'> & { deviceMetadata: RegistrationMetadata };

export const useRegister = (onSuccessCallback?: () => void) => {
  const { mutate: autoLogin } = useLogin(onSuccessCallback);
  const { t } = useLocalization();
  const { showErrorMessage } = useExtendedSnackbar();
  const [invalidPromoCode, setInvalidPromoCode] = useState<boolean>(false);
  const apiResponse = useMutation<RegisterResponse, ErrorObject, RegisterSubmitData>({
    mutationFn: (data: RegisterSubmitData): Promise<RegisterResponse> => {
      const { turnstileToken, ...registrationData } = data;
      const payload: RegisterPayload = {
        ...registrationData,
        deviceMetadata: getRegistrationMetadata(),
      };

      return postData('/customer/register', payload, 'crm', getTurnstileHeaders(turnstileToken));
    },
    onSuccess: (_, data) => {
      autoLogin({
        email: typeof data.email === 'string' ? data.email : '',
        password: typeof data.password === 'string' ? data.password : '',
      });
    },
    onError: (error) => {
      let errorMessage = t('register.validation.unexpectedErrorOccurred');
      if (error.response?.data?.errors) {
        if (error.response.data.errors[0].code === 'INVALID_CODE') {
          setInvalidPromoCode(true);
        }
        errorMessage = error.response.data.errors[0].code || error.response.data.errors[0].error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      showErrorMessage(errorMessage);
    },
  });
  return { ...apiResponse, invalidPromoCode };
};

export type UserRole = {
  id: string;
  name?: string;
};

export type BOUserResponse = {
  email: string;
  firstName: string;
  id: string;
  isActive: boolean;
  lastName: string;
  marketplaceId: string;
  role: UserRole;
  teamLeadId: string;
  userId: string;
  userName?: string;
  fullName: string;
};

export const useRegisterBOUser = (endpoint: string) => {
  const { t } = useLocalization();
  return useMutation<BOUserResponse, ErrorObject, BOUserFormData>({
    mutationFn: (payloadData: BOUserFormData): Promise<BOUserResponse> => {
      return postData(endpoint, payloadData, 'crm');
    },
    onSuccess: (_responseData, payloadData) => {
      enqueueSnackbar(
        t('responseMessages.userCreatedSuccessfully', { user: t(payloadData.groupName ?? 'roles.player') }),
        {
          variant: 'success',
        }
      );
    },
    onError: (error) => {
      let errorMessage = t('responseMessages.unexpectedErrorOccurred');
      if (error.response?.data?.errors) {
        errorMessage = error.response.data.errors[0].error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

export const useManagers = (enabled: boolean) => {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['managers'],
    queryFn: async (): Promise<PaginatedData<BOUserResponse>> => {
      return getData(
        '/agent/search',
        {
          roleTypes: ['Manager'],
          page: 1,
          limit: 100,
        },
        'crm',
        { agentId: userId, role: 'Administrator' }
      );
    },
    select: (data) => data.items,
    enabled,
  });
};

const getProfileSchema = (t: TFunction, signUpFormFields: SignUpFormField[] = []) => {
  const fieldSchemas = createRegisterFieldsSchemas(t);
  const schemaFields: Record<string, z.ZodType<string | boolean | undefined, unknown>> = {};

  DEFAULT_MY_ACCOUNT_FIELDS.forEach((fieldName) => {
    const fieldSchema = fieldSchemas[fieldName as FieldSchemaKey];

    if (fieldSchema) {
      const isRequired = !['country', 'currency', 'email'].includes(fieldName);
      schemaFields[fieldName] = fieldSchema(isRequired);
    }
  });

  signUpFormFields.forEach((field) => {
    if (DEFAULT_MY_ACCOUNT_FIELDS.includes(field.name)) return;

    const fieldSchema = fieldSchemas[field.name as FieldSchemaKey];
    if (fieldSchema) {
      schemaFields[field.name] = fieldSchema(field.required);
    }
  });

  return z.object(schemaFields);
};

// Fields from the sign-up flow that should not be part of the editable user profile.
const EXCLUDED_PROFILE_FIELDS = ['password', 'ageVerified'];

export const useRegisterFields = () => {
  const { data: config } = useMarketplaceConfig();
  const { signUpFormFields = [] } = config?.marketplaceConfig?.registrationFlow || {};
  const profileFormFields = signUpFormFields.filter((field) => !EXCLUDED_PROFILE_FIELDS.includes(field.name));
  const enabledFields = getEnabledFields(profileFormFields);
  const additionalFields = useMemo(
    () => enabledFields.filter((field) => !DEFAULT_MY_ACCOUNT_FIELDS.includes(field)),
    [enabledFields]
  );
  const { t } = useLocalization();

  const profileSchema = getProfileSchema(t, profileFormFields);
  const defaultFieldDefinitions = useMemo<FieldDefinition[]>(
    () => [
      { name: 'country', type: 'text', label: t('country'), disabled: true },
      { name: 'timezone', type: 'text', label: t('timezone'), disabled: true, defaultValue: getTimeZone() },
      { name: 'currency', type: 'text', label: t('currency'), disabled: true },
      { name: 'phone', type: 'text', label: t('phoneNumber') },
      { name: 'firstName', type: 'text', label: t('firstName') },
      { name: 'lastName', type: 'text', label: t('lastName') },
      { name: 'dateOfBirth', type: 'date', label: t('dateOfBirth') },
      { name: 'email', type: 'text', label: t('email'), disabled: true },
    ],
    [t]
  );

  const additionalFieldDefinitions = useMemo<FieldDefinition[]>(() => {
    const fieldMap: Record<string, FieldDefinition> = {
      address: { name: 'address', type: 'text', label: t('register.address') },
      city: { name: 'city', type: 'text', label: t('register.city') },
      region: { name: 'region', type: 'text', label: t('register.region') },
      zip: { name: 'zip', type: 'text', label: t('register.zip') },
    };

    return additionalFields
      .map((fieldName) => fieldMap[fieldName])
      .filter(Boolean)
      .sort((a, b) => {
        const orderA = signUpFormFields.find((f) => f.name === a.name)?.order ?? 0;
        const orderB = signUpFormFields.find((f) => f.name === b.name)?.order ?? 0;
        return orderA - orderB;
      });
  }, [additionalFields, signUpFormFields, t]);
  return { additionalFields, profileSchema, defaultFieldDefinitions, additionalFieldDefinitions };
};
